/**
 * #606 — AudioWorklet PCM capture, drop-in replacement for MediaRecorder
 * for the streaming-to-Deepgram path.
 *
 * Two-tier API:
 *
 *   createPcmAudioContext()         — call-level constructor. Creates ONE
 *                                     AudioContext at call start (inside
 *                                     the user-gesture window from the
 *                                     answer-tap), registers the worklet
 *                                     module, returns the context. Owner
 *                                     keeps it alive across all turns of
 *                                     the call and closes it at hangup.
 *
 *   createPcmCapture(stream, opts)  — per-turn audio graph builder. Takes
 *                                     a pre-built context (preferred path,
 *                                     2026-05-11 lifecycle rework) OR
 *                                     creates its own (legacy fallback,
 *                                     for callers that haven't migrated).
 *                                     Builds source + worklet node graph;
 *                                     tearing it down on stop() leaves the
 *                                     external context alive for the next
 *                                     turn.
 *
 * Why the two-tier design (2026-05-11 lifecycle rework):
 *
 * The old single-call API created a fresh AudioContext per turn. This bit
 * us in two ways on staging:
 *   1. Turn 2 of a call: the new AudioContext was 'suspended' (no fresh
 *      gesture in the last few seconds) and the worklet emitted all-zero
 *      PCM. Diagnosed with Bas's session producing 115200 bytes of zeros.
 *   2. Mid-call drift: even when turn 1 + 2 worked, after several minutes
 *      the per-turn AudioContext sometimes degraded silently (likely
 *      browser-driven suspend during a pause, OS audio-device switch, or
 *      power-saver). Subsequent turns then produced zeros until the user
 *      hung up.
 *
 * The new design pins ONE AudioContext to the call's lifetime. It's
 * created inside the user-gesture handler (`handleCall`) so the browser
 * starts it in 'running' state. A watchdog in TabletApp.js monitors the
 * context's state every second and attempts `.resume()` if it drifts —
 * if recovery fails repeatedly, the caller marks PCM degraded and
 * falls back to MediaRecorder for the rest of the call.
 *
 * Returns null from either function when the browser doesn't support
 * AudioWorklet, when the module fails to load, or when audio graph
 * construction fails. Callers must check for null and fall back to
 * MediaRecorder.
 */

const WORKLET_URL = '/audio/pcm-worklet.js';

/**
 * Create the long-lived AudioContext for this call. Must be invoked
 * INSIDE a user-gesture handler (button click) so the browser starts
 * the context in 'running' state and doesn't require a later
 * `.resume()` against an expired gesture window.
 *
 * (#811) Always uses the browser's native sample rate (typically 48000
 * or 44100). The pre-#811 code tried to force 16000 to match what
 * Deepgram expects; when the browser refused (Safari + many Androids),
 * the fallback constructor ran at native rate and the worklet
 * decimated without an anti-aliasing filter — producing aliased noise
 * that Deepgram interpreted as garbled speech. Native-rate-throughout
 * eliminates the aliasing risk; the server tells Deepgram the actual
 * rate via the `sample_rate=` URL parameter and Deepgram's own
 * resampling (proper, with anti-aliasing) does the rest.
 *
 * Returns the AudioContext on success, null on any failure. Caller
 * (TabletApp.js) is responsible for:
 *   - reading `audioContext.sampleRate` and signalling it to the server
 *     via the WS `start` message's `audio_sample_rate` field, AND
 *   - closing the context on hangup.
 */
export async function createPcmAudioContext() {
  if (typeof AudioContext === 'undefined' && typeof webkitAudioContext === 'undefined') {
    return null;
  }
  const Ctor = window.AudioContext || window.webkitAudioContext;
  let audioContext = null;
  try {
    // #811 — no explicit `sampleRate`. Whatever the browser gives us
    // is what we ship. Removes the decimation path that produced
    // aliased PCM on browsers that refused the 16 kHz construction.
    audioContext = new Ctor();
  } catch (e) {
    console.warn('[pcm-capture] AudioContext construction failed:', e);
    return null;
  }
  // Resume immediately if suspended — we're inside the user-gesture
  // window, so the browser will honour the request. This is the
  // window we cannot reproduce on later turns, so we MUST succeed here.
  try {
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }
  } catch (e) {
    console.warn('[pcm-capture] AudioContext resume on construct failed:', e);
  }
  if (audioContext.state !== 'running') {
    console.warn(
      `[pcm-capture] AudioContext not running after construct (state=${audioContext.state}) ` +
      `— browser refused gesture. Falling back to MediaRecorder for this call.`
    );
    try { await audioContext.close(); } catch {}
    return null;
  }
  // Register the worklet module once per context. We can't do this
  // before the context is running because addModule races the audio
  // worker boot.
  try {
    await audioContext.audioWorklet.addModule(WORKLET_URL);
  } catch (e) {
    console.warn('[pcm-capture] worklet module load failed:', e);
    try { await audioContext.close(); } catch {}
    return null;
  }
  console.log(
    `[pcm-capture] call-level AudioContext ready ` +
    `(state=${audioContext.state}, sampleRate=${audioContext.sampleRate})`
  );
  return audioContext;
}

/**
 * Build the per-turn audio graph (mic stream → worklet → onChunk).
 *
 * Required:
 *   stream                : MediaStream from getUserMedia
 *   opts.audioContext     : pre-built AudioContext (new path, 2026-05-11)
 *                           OR omit/null to fall back to per-turn creation
 *                           (legacy path; deprecated, kept for backward
 *                           compat with any caller that hasn't migrated)
 *   opts.onChunk          : callback fired per ~20ms PCM frame, receives a Blob
 *   opts.onError          : callback for processor errors
 *
 * Returns a MediaRecorder-shaped object {state, start, stop, on*}
 * or null on construction failure.
 *
 * Key lifecycle change: when audioContext is provided externally, stop()
 * tears down only the source + worklet nodes — the context stays alive
 * for the next turn. When audioContext is null/omitted, stop() also
 * closes the context (legacy behaviour).
 */
export async function createPcmCapture(stream, { audioContext, onChunk, onError } = {}) {
  if (typeof AudioContext === 'undefined' && typeof webkitAudioContext === 'undefined') {
    return null;
  }

  // Track whether we own the context (and therefore must close it on
  // stop) or it's borrowed from the caller (we leave it alive).
  let ownsContext = false;
  let workletNode = null;
  let sourceNode = null;
  let stopped = false;

  if (audioContext) {
    // Borrowed path — caller created the context and will close it on
    // hangup. Defensive: re-check state in case it drifted suspended
    // between caller's last check and now.
    if (audioContext.state === 'suspended') {
      try { await audioContext.resume(); } catch {}
    }
    if (audioContext.state !== 'running') {
      console.warn(
        `[pcm-capture] borrowed AudioContext is not running ` +
        `(state=${audioContext.state}) — caller should fall back to MediaRecorder`
      );
      return null;
    }
  } else {
    // Legacy path — create our own context. Kept for callers that
    // haven't migrated to the call-level pattern.
    ownsContext = true;
    const Ctor = window.AudioContext || window.webkitAudioContext;
    try {
      // #811 — native rate, no decimation. See createPcmAudioContext.
      audioContext = new Ctor();
    } catch (e) {
      console.warn('[pcm-capture] AudioContext construction failed:', e);
      return null;
    }
    try {
      await audioContext.audioWorklet.addModule(WORKLET_URL);
    } catch (e) {
      console.warn('[pcm-capture] worklet module load failed:', e);
      try { await audioContext.close(); } catch {}
      return null;
    }
    try {
      if (audioContext.state === 'suspended') await audioContext.resume();
    } catch (e) {
      console.warn('[pcm-capture] AudioContext resume failed:', e);
    }
    if (audioContext.state !== 'running') {
      console.warn(
        `[pcm-capture] legacy-path AudioContext not running (state=${audioContext.state}) ` +
        `— PCM frames would be silent. Falling back to MediaRecorder.`
      );
      try { await audioContext.close(); } catch {}
      return null;
    }
  }

  const capture = {
    state: 'inactive',
    start: () => {},                 // already started; kept for API parity
    stop: null,                      // set below
    ondataavailable: null,           // set by caller, mimics MediaRecorder
    onerror: null,                   // set by caller
    onstop: null,                    // set by caller
  };

  try {
    sourceNode = audioContext.createMediaStreamSource(stream);
    workletNode = new AudioWorkletNode(audioContext, 'pcm-worklet');

    workletNode.port.onmessage = (e) => {
      if (stopped) return;
      try {
        const blob = new Blob([e.data], { type: 'application/octet-stream' });
        if (capture.ondataavailable) capture.ondataavailable({ data: blob });
        if (typeof onChunk === 'function') onChunk(blob);
      } catch (err) {
        console.warn('[pcm-capture] frame dispatch failed:', err);
      }
    };

    workletNode.onprocessorerror = (err) => {
      console.error('[pcm-capture] processor error:', err);
      if (typeof onError === 'function') onError(err);
      if (capture.onerror) capture.onerror(err);
    };

    sourceNode.connect(workletNode);
    // Intentionally not connected to destination — we don't want to
    // monitor the mic through the speakers.

    capture.state = 'recording';
  } catch (e) {
    console.warn('[pcm-capture] audio graph setup failed:', e);
    try { workletNode && workletNode.disconnect(); } catch {}
    try { sourceNode && sourceNode.disconnect(); } catch {}
    if (ownsContext) {
      try { await audioContext.close(); } catch {}
    }
    return null;
  }

  capture.stop = async () => {
    if (stopped) return;
    stopped = true;
    capture.state = 'inactive';
    try { workletNode && workletNode.port && workletNode.port.close(); } catch {}
    try { workletNode && workletNode.disconnect(); } catch {}
    try { sourceNode && sourceNode.disconnect(); } catch {}
    // Only close the context if we created it. When it was passed in,
    // the caller (TabletApp.js) owns its lifetime — we leave it alive
    // for the next turn.
    if (ownsContext) {
      try { await audioContext.close(); } catch {}
    }
    if (typeof capture.onstop === 'function') {
      Promise.resolve().then(() => {
        try { capture.onstop(); } catch (e) { console.warn(e); }
      });
    }
  };

  return capture;
}
